import React, { useCallback, useEffect, useMemo } from 'react';
import { Card, CardBody } from 'reactstrap';
import { Link, useHistory, useParams } from 'react-router-dom';
import { faSliders } from '@fortawesome/pro-solid-svg-icons';

import { Icon } from 'js/components';
import { useNotify } from 'js/hooks/useNotify';
import { useQuery } from 'js/hooks/useQuery';
import { useWorkspaceRubrics } from 'js/hooks/useWorkspaceRubrics';
import { useSelector } from 'react-redux';

import { OrgRubricSchemesProvider } from './OrgRubricSchemesContext';
import OrgRubricSchemesGrid from './OrgRubricSchemesGrid';
import OrgRubricPicker from './OrgRubricPicker';
import {
  ORG_RUBRIC_SCHEMES_QUERY_IDLE,
  orgManageRubricsPath,
  orgRubricSchemesQueryId,
} from './orgRubricSchemesUtils';

import { faHandPointUp } from '@fortawesome/pro-regular-svg-icons';

import styles from './OrgWeights.module.scss';

function OrgRubricSchemesEditor({
  workspaceId,
  organization,
  rubrics,
  rubricId,
  rubric,
  rubricNotFound,
  hasNoRubrics,
  schemesPayload,
  schemesLoading,
  schemesHasLoaded,
  schemesError,
  rubricsLoading,
  rubricsHasLoaded,
  rubricsError,
  onPersistTeamScheme,
  onRubricChange,
}) {
  const rubricDocument = rubric?.document ?? null;

  if (hasNoRubrics) {
    return (
      <div className="media text-muted mt-4 mb-4">
        <Icon icon={faSliders} size="4x" className="mr-3" fixedWidth />
        <div className="media-body">
          <p>You haven't created any rubrics yet.</p>
          <p className="mb-0">
            <Link to={`/w/${workspaceId}/rubrics/new`}>Create a rubric</Link> to
            get started, then come back here to set the weight schemes to use
            for each team in this organization.
          </p>
        </div>
      </div>
    );
  }

  if (rubricsError || schemesError) {
    return <p className="text-danger mb-0">{rubricsError || schemesError}</p>;
  }

  let body = (
    <p className={styles['scheme-select-pointer']}>
      <Icon icon={faHandPointUp} size="2x" className="icon-left text-info" />
      Select a rubric to configure which weight scheme to use for each team.
    </p>
  );

  if (rubricNotFound) {
    body = (
      <div className="media text-muted mt-4 mb-4">
        <Icon icon={faSliders} size="4x" className="mr-3" fixedWidth />
        <div className="media-body">
          <p>We couldn't find that rubric.</p>
          <p className="mb-0">
            It may have been deleted, or maybe your link just got garbled. Try
            choosing a different one.
          </p>
        </div>
      </div>
    );
  } else if (rubricId && rubric?.document) {
    body = (
      <>
        {schemesLoading || !schemesHasLoaded ? (
          <p className="text-muted small mb-2">Loading team weight schemes…</p>
        ) : null}
        <OrgRubricSchemesGrid
          organization={organization}
          rubricDocument={rubric.document}
          rubricId={rubricId}
        />
      </>
    );
  }

  return (
    <OrgRubricSchemesProvider
      organization={organization}
      rubricId={rubricId}
      rubricDocument={rubricDocument}
      schemesPayload={schemesPayload}
      onPersistTeamScheme={onPersistTeamScheme}
    >
      {rubrics.length > 0 ? (
        <div className={styles.toolbar}>
          <OrgRubricPicker
            rubrics={rubrics}
            rubricId={rubricId}
            onRubricChange={onRubricChange}
            loading={rubricsLoading || !rubricsHasLoaded}
            error={rubricsError}
          />
        </div>
      ) : null}
      {body}
    </OrgRubricSchemesProvider>
  );
}

function OrgRubricSchemes({
  organization,
  lookupOrgRubricWeightSchemes,
  listWorkspaceRubrics,
  onPersistTeamScheme,
}) {
  const notify = useNotify();
  const history = useHistory();
  const { workspaceId, orgId, rubricId: rubricIdParam } = useParams();

  const {
    rubrics,
    isLoading: rubricsLoading,
    hasLoaded: rubricsHasLoaded,
    error: rubricsQueryError,
  } = useWorkspaceRubrics(workspaceId, listWorkspaceRubrics);

  const rubricsLoadError = rubricsQueryError
    ? 'Unable to load rubrics for this workspace.'
    : null;

  const rubric = useMemo(() => {
    if (!rubricIdParam) return null;
    return rubrics.find((r) => r.id === rubricIdParam) ?? null;
  }, [rubrics, rubricIdParam]);

  const rubricId = rubric?.id ?? '';
  const pickerRubricId = rubricId || rubricIdParam || '';
  const hasNoRubrics = Boolean(
    rubricsHasLoaded &&
      !rubricsLoading &&
      !rubricsLoadError &&
      rubrics.length === 0
  );

  const rubricNotFound = Boolean(
    !hasNoRubrics &&
      rubricIdParam &&
      rubricsHasLoaded &&
      !rubricsLoading &&
      !rubric
  );

  const handleRubricChange = useCallback(
    (nextRubricId) => {
      history.push(orgManageRubricsPath(workspaceId, orgId, nextRubricId));
    },
    [history, workspaceId, orgId]
  );

  const schemesQueryId = useMemo(
    () =>
      organization?.id && rubricId
        ? orgRubricSchemesQueryId(organization.id, rubricId)
        : ORG_RUBRIC_SCHEMES_QUERY_IDLE,
    [organization?.id, rubricId]
  );

  const schemesQueryError = useSelector(
    (state) => state.queries[schemesQueryId]?.error
  );

  const schemesLoadError = schemesQueryError
    ? 'Unable to load rubric weight schemes for this organization.'
    : null;

  const loadSchemesQuery = useCallback(
    (qid) => {
      if (
        qid === ORG_RUBRIC_SCHEMES_QUERY_IDLE ||
        !organization?.id ||
        !rubricId
      ) {
        return Promise.resolve(null);
      }
      return lookupOrgRubricWeightSchemes(organization.id, rubricId, qid);
    },
    [lookupOrgRubricWeightSchemes, organization?.id, rubricId]
  );

  const {
    result: schemesPayload,
    refresh: refreshSchemes,
    isLoading: schemesLoading,
    hasLoaded: schemesHasLoaded,
  } = useQuery({
    queryId: schemesQueryId,
    query: loadSchemesQuery,
  });

  useEffect(() => {
    refreshSchemes();
  }, [refreshSchemes]);

  const persistTeamScheme = useCallback(
    async (orgId, selectedRubricId, teamId, body) => {
      try {
        await onPersistTeamScheme(orgId, selectedRubricId, teamId, body);
        void refreshSchemes();
      } catch (err) {
        const message =
          err?.response?.data?.errors?.[0]?.detail ??
          err?.response?.data?.message ??
          err?.message ??
          'Unable to save rubric weight scheme.';
        notify({ type: 'danger', message });
        throw err;
      }
    },
    [onPersistTeamScheme, refreshSchemes, notify]
  );

  if (!organization?.id) {
    return null;
  }

  return (
    <div className="manage-org">
      <Card>
        <CardBody>
          <OrgRubricSchemesEditor
            workspaceId={workspaceId}
            organization={organization}
            rubrics={rubrics}
            rubricId={pickerRubricId}
            rubric={rubric}
            rubricNotFound={rubricNotFound}
            hasNoRubrics={hasNoRubrics}
            schemesPayload={schemesPayload}
            schemesLoading={schemesLoading}
            schemesHasLoaded={schemesHasLoaded}
            schemesError={schemesLoadError}
            rubricsLoading={rubricsLoading}
            rubricsHasLoaded={rubricsHasLoaded}
            rubricsError={rubricsLoadError}
            onPersistTeamScheme={persistTeamScheme}
            onRubricChange={handleRubricChange}
          />
        </CardBody>
      </Card>
    </div>
  );
}

export default OrgRubricSchemes;
